import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UserContext } from '../../../context/UserContext';
import { useInVenueUsers } from '../../../context/useInVenueUsers';
import axios from 'axios';
import PaymentModal from '../DispenserComponents/PaymentModal';
import { formatApiDateTime } from '../../../lib/helpers';

/**
 * Carritos = unpaid transactions (source of truth: same as Contabilidad).
 * Shows draft transactions (Dispensario / Productos) with estado "Debe".
 * Same structure as Contabilidad: one row per transaction, expand/collapse for products.
 */
export default function Carritos() {
  const [token, , , venueID] = useContext(UserContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterUserId = searchParams.get('user_id') || null;
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentTx, setPaymentTx] = useState(null);
  const { updateUserCredit, refresh: refreshUsersInClub } = useInVenueUsers(token, venueID);

  const fetchTransactions = useCallback(async (silent = false) => {
    if (!venueID || !token) return;
    if (!silent) setLoading(true);
    try {
      const res = await axios.get('/v1/venue-draft-carts', {
        params: { venue_id: venueID },
        headers: { Authorization: `Bearer ${token}` },
      });
      setTransactions(res.data?.draft_carts || []);
    } catch (e) {
      console.warn('Fetch venue draft carts failed:', e);
      setTransactions([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [venueID, token]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchTransactions();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [fetchTransactions]);

  // ── Edición de líneas del carrito persistido (misma UX que el carrito del Dispensario/Barra) ──
  // +/- en productos por unidad, borrar línea (gramos o unidades), vaciar el carrito.
  // Cada acción re-postea el carrito completo a /v1/draft-cart (que libera y vuelve a
  // reservar stock); vaciar = DELETE. Optimista en local + refetch silencioso para reconciliar.
  const [savingCartId, setSavingCartId] = useState(null);

  const sameLine = (a, b) =>
    String(a.product_id) === String(b.product_id) &&
    String(a.location_id ?? '') === String(b.location_id ?? '');

  // % de descuento del socio a partir de su etiqueta ("50%" → 0.5). Estable, no depende
  // del discount_amount (que ya puede incluir descuento manual).
  const parsePct = (s) => { const m = String(s || '').match(/(\d+)%/); return m ? Number(m[1]) / 100 : 0; };

  // Recalcula subtotal/descuento/total de un carrito tras editar sus líneas (preview local).
  // El descuento por línea = socio (etiqueta) + manual de la línea (clamp 100%).
  const recomputeCart = (tx, newItems) => {
    const subtotal = newItems.reduce(
      (s, i) => s + (Number(i.quantity) || 0) * (parseFloat(i.unit_price) || 0), 0
    );
    let memberRate = parsePct(tx.discount_label);
    // Fallback para carritos sin etiqueta: ratio original (válido si aún no hay manual).
    if (!memberRate && Number(tx.subtotal) > 0 && !newItems.some((i) => i.manual_discount)) {
      memberRate = Number(tx.discount_amount || 0) / Number(tx.subtotal);
    }
    let discount_amount = 0;
    newItems.forEach((i) => {
      const lineSub = (Number(i.quantity) || 0) * (parseFloat(i.unit_price) || 0);
      const manualRate = Math.min(1, Math.max(0, (Number(i.manual_discount) || 0) / 100));
      const effRate = Math.min(1, memberRate + manualRate);
      discount_amount += lineSub * effRate;
    });
    const amount = Math.max(0, subtotal - discount_amount);
    return { ...tx, items: newItems, subtotal, discount_amount, amount, total: amount, items_count: newItems.length };
  };

  const persistCart = useCallback(async (tx, newItems) => {
    const items = newItems
      .filter((i) => (Number(i.quantity) || 0) > 0)
      .map((i) => ({
        product_id: i.product_id,
        quantity: Number(i.quantity),
        unit_price: Number(i.unit_price) || 0,
        real_weight: i.real_weight != null ? Number(i.real_weight) : null,
        location_id: i.location_id ?? null,
      }));
    setSavingCartId(tx.id);
    try {
      if (items.length === 0) {
        await fetch(
          `/v1/draft-cart?user_id=${tx.user_id}&venue_id=${venueID}&cart_type=${tx.cart_type}`,
          { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        const res = await fetch('/v1/draft-cart', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ user_id: tx.user_id, venue_id: venueID, cart_type: tx.cart_type, items }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          const detail = err?.detail;
          throw new Error(typeof detail === 'string' ? detail : (detail?.msg || 'No se pudo actualizar el carrito'));
        }
      }
    } catch (e) {
      alert(e.message || 'No se pudo actualizar el carrito');
    } finally {
      setSavingCartId(null);
      await fetchTransactions(true);
    }
  }, [venueID, token, fetchTransactions]);

  const editCartLines = (tx, newItems) => {
    setTransactions((prev) => prev.map((t) => (t.id === tx.id ? recomputeCart(t, newItems) : t)));
    persistCart(tx, newItems);
  };

  const handleItemQty = (tx, item, delta) => {
    const newItems = (tx.items || [])
      .map((i) => (sameLine(i, item) ? { ...i, quantity: +(Number(i.quantity) + delta).toFixed(3) } : i))
      .filter((i) => (Number(i.quantity) || 0) > 0);
    editCartLines(tx, newItems);
  };

  const handleItemRemove = (tx, item) => {
    const newItems = (tx.items || []).filter((i) => !sameLine(i, item));
    editCartLines(tx, newItems);
  };

  // Descuento manual de la línea (%). Solo estado local (el draft del servidor no lo
  // guarda); se recalcula el total y viaja al cobro (PaymentModal → make-purchase).
  // No re-postea el carrito para no perderlo en el refetch.
  const handleItemDiscount = (tx, item, value) => {
    const raw = value === '' || value == null ? undefined : Math.min(100, Math.max(0, Number(value)));
    const newItems = (tx.items || []).map((i) => (sameLine(i, item) ? { ...i, manual_discount: raw } : i));
    setTransactions((prev) => prev.map((t) => (t.id === tx.id ? recomputeCart(t, newItems) : t)));
  };

  const handleClearCart = (tx) => {
    if (!window.confirm('¿Vaciar este carrito? Se devolverá al stock todo lo reservado.')) return;
    editCartLines(tx, []);
  };

  // Lista de líneas editable (reutilizada en tarjeta móvil y tabla desktop)
  const renderItemsEditor = (tx) => {
    const saving = savingCartId === tx.id;
    if (!tx.items || tx.items.length === 0) {
      return <div className="mt-2 text-gray-400 text-xs">Sin items</div>;
    }
    return (
      <div className="mt-2 p-2 bg-gray-50 rounded text-xs space-y-2">
        {tx.items.map((item, index) => {
          const isWeight = item.unit_type === 'weight';
          const lineTotal = (Number(item.quantity) || 0) * parseFloat(item.unit_price || 0);
          return (
            <div key={index} className="border-b border-gray-200 pb-2 last:border-b-0 last:pb-0">
              {/* Nombre + borrar */}
              <div className="flex items-start justify-between gap-2">
                <span className="font-medium truncate">{item.product_name || `Producto #${item.product_id}`}</span>
                <button type="button" disabled={saving} onClick={() => handleItemRemove(tx, item)} title="Eliminar línea" aria-label="Eliminar línea" className="flex-shrink-0 text-red-600 hover:text-red-700 p-0.5 disabled:opacity-50">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                </button>
              </div>
              {/* Lista: una fila por dato (etiqueta · valor) */}
              <div className="mt-1 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Precio</span>
                  <span className="text-gray-700 whitespace-nowrap">€{parseFloat(item.unit_price || 0).toFixed(2)}/{isWeight ? 'g' : 'u'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Cantidad</span>
                  {isWeight ? (
                    // Por gramo: el peso se fija con la báscula al añadir; aquí no se ajusta.
                    <span className="inline-flex items-center rounded-md border border-gray-300 px-2 py-1 tabular-nums">
                      {item.quantity}<small className="ml-0.5 text-gray-500">g</small>
                    </span>
                  ) : (
                    <div className="inline-flex items-center rounded-md border border-gray-300">
                      <button type="button" disabled={saving} onClick={() => handleItemQty(tx, item, -1)} className="px-2 py-1 text-gray-600 hover:bg-gray-100 disabled:opacity-50" aria-label="Quitar uno">–</button>
                      <span className="px-1 tabular-nums min-w-[40px] text-center">{item.quantity}<small className="ml-0.5 text-gray-500">u</small></span>
                      <button type="button" disabled={saving} onClick={() => handleItemQty(tx, item, 1)} className="px-2 py-1 text-gray-600 hover:bg-gray-100 disabled:opacity-50" aria-label="Añadir uno">+</button>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Dto.</span>
                  {/* Descuento manual de la línea (%): se suma al del socio. 100% = regalar. */}
                  <span className="inline-flex items-center gap-1 text-gray-500">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={item.manual_discount ?? ''}
                      onChange={(e) => handleItemDiscount(tx, item, e.target.value)}
                      placeholder="0"
                      className="w-12 rounded border border-gray-300 px-1 py-0.5 text-right tabular-nums focus:border-lime-400 focus:outline-none"
                    />
                    <span>%</span>
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Total</span>
                  <span className="font-semibold text-gray-900">€{lineTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const handlePagar = (tx) => {
    setPaymentTx(tx);
  };

  // "Pagar Todo" — combine all carts for a given user into one payment
  const handlePagarTodo = (userId) => {
    const userTxs = transactions.filter((t) => t.user_id === userId);
    if (userTxs.length === 0) return;

    const combinedItems = userTxs.flatMap((t) => (t.items || []).map((i) => ({ ...i, _cartType: t.cart_type })));
    const combinedTotal = userTxs.reduce((s, t) => s + parseFloat(t.amount ?? t.total ?? 0), 0);
    const combinedSubtotal = userTxs.reduce((s, t) => s + parseFloat(t.subtotal ?? t.amount ?? t.total ?? 0), 0);
    const combinedDiscountAmount = userTxs.reduce((s, t) => s + parseFloat(t.discount_amount ?? 0), 0);
    const cartTypes = [...new Set(userTxs.map((t) => t.cart_type))];
    const discountLabels = [...new Set(userTxs.map((t) => t.discount_label).filter(Boolean))];

    setPaymentTx({
      user_id: userId,
      user_name: userTxs[0].user_name,
      user_email: userTxs[0].user_email,
      items: combinedItems,
      amount: combinedTotal,
      subtotal: combinedSubtotal,
      discount_amount: combinedDiscountAmount,
      discount_label: discountLabels.length > 0 ? discountLabels[0] : null,
      // 'combined' cuando hay 2+ tipos → el backend consume AMBOS drafts (dispensario
      // + productos) y no re-descuenta. Un solo tipo = ese cart_type.
      cart_type: cartTypes.length === 1 ? cartTypes[0] : 'combined',
      _allCartTypes: cartTypes,
      _isCombined: true,
    });
  };

  const handlePaymentComplete = async (result) => {
    const tx = paymentTx;
    if (
      result?.method === 'credit' &&
      tx?.user_id != null &&
      result.newBalance != null &&
      Number.isFinite(Number(result.newBalance))
    ) {
      updateUserCredit(tx.user_id, result.newBalance);
      await refreshUsersInClub({
        creditPatch: { userId: tx.user_id, balance: result.newBalance },
      });
    }
    if (paymentTx?._isCombined && paymentTx?._allCartTypes) {
      // If combined payment, delete all draft carts for the user
      for (const ct of paymentTx._allCartTypes) {
        try {
          await fetch(
            `/v1/draft-cart?user_id=${paymentTx.user_id}&venue_id=${venueID}&cart_type=${ct}`,
            { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (e) {
          console.warn('Delete draft cart failed:', e);
        }
      }
    } else if (paymentTx?.cart_type) {
      // If single payment, delete the specific draft cart
      try {
        await fetch(
          `/v1/draft-cart?user_id=${paymentTx.user_id}&venue_id=${venueID}&cart_type=${paymentTx.cart_type}`,
          { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (e) {
        console.warn('Delete draft cart failed:', e);
      }
    }
    setPaymentTx(null);
    await fetchTransactions();
    navigate('/control');
  };

  // Group transactions by user_id to show "Pagar Todo" when a user has multiple carts
  const userCartCounts = {};
  for (const tx of transactions) {
    userCartCounts[tx.user_id] = (userCartCounts[tx.user_id] || 0) + 1;
  }

  // Filter by user when coming from Entradas (e.g. /carritos?user_id=xxx)
  const displayedTransactions = filterUserId
    ? transactions.filter((t) => t.user_id === filterUserId)
    : transactions;
  const filterUserName = filterUserId && displayedTransactions.length
    ? displayedTransactions[0].user_name || displayedTransactions[0].user_email
    : null;

  const formatDate = (dateString) =>
    formatApiDateTime(dateString, { fallback: '—' });

  const getTypeLabel = (cartType) =>
    cartType === 'productos' ? 'Productos' : 'Dispensario';

  const getTypeColor = (cartType) =>
    cartType === 'productos' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800';

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center p-8">
          <p className="text-gray-500">Cargando...</p>
        </div>
      );
    }
    if (transactions.length === 0) {
      return (
        <div className="bg-white p-8 rounded-lg shadow border border-gray-200 text-center text-gray-500">
          No hay carritos pendientes de pago.
        </div>
      );
    }
    if (displayedTransactions.length === 0) {
      return (
        <div className="bg-white p-8 rounded-lg shadow border border-gray-200 text-center text-gray-500">
          <p>Este socio no tiene carritos pendientes.</p>
          <button
            type="button"
            onClick={() => setSearchParams({})}
            className="mt-2 text-purple-600 hover:text-purple-800 font-medium"
          >
            Ver todos los carritos
          </button>
        </div>
      );
    }
    return (
      <>
        {/* Vista tarjetas: móvil + tablet (vertical y apaisado); tabla solo 2xl+ */}
        <div className="block 2xl:hidden space-y-3">
          {displayedTransactions.map((tx) => (
            <div
              key={tx.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 hover:shadow-md hover:border-purple-300 transition-all duration-200"
            >
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <div className="text-xs text-gray-500 font-mono">
                    #{tx.id?.toString().slice(0, 8) || '—'}
                  </div>
                  <div className="text-sm font-semibold text-gray-900 truncate">
                    {tx.user_name || tx.user_email || `Usuario #${tx.user_id?.toString().slice(0, 8)}` || '—'}
                  </div>
                  {tx.user_name && tx.user_email && (
                    <div className="text-xs text-gray-500 truncate">
                      {tx.user_email}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getTypeColor(tx.cart_type)}`}>
                    {getTypeLabel(tx.cart_type)}
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    €{parseFloat(tx.amount ?? tx.total ?? 0).toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-600 mb-2">
                <span>{formatDate(tx.timestamp)}</span>
                <span className="px-2 py-0.5 text-[11px] font-semibold rounded-full bg-amber-100 text-amber-800">
                  {tx.estado || 'Debe'}
                </span>
              </div>

              {/* Botones antes del detalle de productos: siempre visibles sin scroll */}
              <div className="flex flex-wrap items-center gap-2 justify-end mb-2 pb-2 border-b border-gray-100">
                <button
                  type="button"
                  disabled={savingCartId === tx.id}
                  onClick={() => handleClearCart(tx)}
                  className="mr-auto px-3 py-2 text-red-600 hover:text-red-700 text-xs font-semibold disabled:opacity-50"
                >
                  Vaciar
                </button>
                <button
                  type="button"
                  onClick={() => handlePagar(tx)}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded"
                >
                  Pagar
                </button>
                {userCartCounts[tx.user_id] > 1 && (
                  <button
                    type="button"
                    onClick={() => handlePagarTodo(tx.user_id)}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded"
                  >
                    Pagar Todo
                  </button>
                )}
              </div>

              <div className="mb-2">
                {tx.items && tx.items.length > 0 ? (
                  <details className="cursor-pointer">
                    <summary className="text-blue-600 hover:text-blue-800 text-xs">
                      {tx.items.length} producto{tx.items.length !== 1 ? 's' : ''}
                    </summary>
                    {renderItemsEditor(tx)}
                  </details>
                ) : (
                  <span className="text-gray-400 text-xs">Sin items</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Vista tabla: solo pantallas muy anchas (2xl+); tablet apaisado usa tarjetas */}
        <div className="hidden 2xl:block bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-gray-700">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                <tr>
                  <th scope="col" className="py-3 px-4">ID</th>
                  <th scope="col" className="py-3 px-4">Usuario</th>
                  <th scope="col" className="py-3 px-4">Tipo</th>
                  <th scope="col" className="py-3 px-4">Monto</th>
                  <th scope="col" className="py-3 px-4">Fecha</th>
                  <th scope="col" className="py-3 px-4">Estado</th>
                  <th scope="col" className="py-3 px-4">Productos</th>
                  <th scope="col" className="py-3 px-4 text-right sticky right-0 z-10 bg-gray-50 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)]">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {displayedTransactions.map((tx) => (
                  <tr
                    key={tx.id}
                    className="group border-b odd:bg-white even:bg-gray-50 hover:bg-gray-100"
                  >
                    <td className="py-3 px-4 font-mono text-sm">
                      #{tx.id?.toString().slice(0, 8) || '—'}
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm font-medium">
                        {tx.user_name || tx.user_email || `Usuario #${tx.user_id?.toString().slice(0, 8)}` || '—'}
                      </div>
                      {tx.user_name && tx.user_email && (
                        <div className="text-xs text-gray-500">{tx.user_email}</div>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(tx.cart_type)}`}>
                        {getTypeLabel(tx.cart_type)}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-semibold">
                      €{parseFloat(tx.amount ?? tx.total ?? 0).toFixed(2)}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {formatDate(tx.timestamp)}
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                        {tx.estado || 'Debe'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {tx.items && tx.items.length > 0 ? (
                        <details className="cursor-pointer">
                          <summary className="text-blue-600 hover:text-blue-800 text-sm">
                            {tx.items.length} producto{tx.items.length !== 1 ? 's' : ''}
                          </summary>
                          {renderItemsEditor(tx)}
                        </details>
                      ) : (
                        <span className="text-gray-400 text-sm">Sin items</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right sticky right-0 z-10 bg-inherit shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)]">
                      <div className="flex flex-col items-end gap-1.5 min-w-[7.5rem]">
                      <button
                        type="button"
                        disabled={savingCartId === tx.id}
                        onClick={() => handleClearCart(tx)}
                        className="w-full px-3 py-1.5 text-red-600 hover:text-red-700 text-sm font-medium disabled:opacity-50 text-left"
                      >
                        Vaciar
                      </button>
                      <button
                        type="button"
                        onClick={() => handlePagar(tx)}
                        className="w-full px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded"
                      >
                        Pagar
                      </button>
                      {userCartCounts[tx.user_id] > 1 && (
                        <button
                          type="button"
                          onClick={() => handlePagarTodo(tx.user_id)}
                          className="w-full px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded"
                        >
                          Pagar Todo
                        </button>
                      )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="p-2 sm:p-4 space-y-3 sm:space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold text-gray-800">Carritos</h1>
          <p className="text-sm text-gray-500 mt-1">
            Transacciones pendientes de pago (Debe). Misma estructura que Contabilidad. Usa &quot;Pagar&quot; para ir a Dispensario o Productos y validar.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {filterUserId && (
            <span className="text-sm text-purple-700 bg-purple-50 px-3 py-1.5 rounded-lg">
              Carritos de {filterUserName || 'este socio'}
              <button
                type="button"
                onClick={() => setSearchParams({})}
                className="ml-2 text-purple-600 hover:text-purple-800 font-medium"
              >
                Ver todos
              </button>
            </span>
          )}
          <button
            type="button"
            onClick={() => fetchTransactions()}
            className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded"
          >
            Recargar
          </button>
        </div>
      </div>

      {renderContent()}

      <PaymentModal
        active={!!paymentTx}
        onClose={() => setPaymentTx(null)}
        onPaymentComplete={handlePaymentComplete}
        items={paymentTx?.items || []}
        total={parseFloat(paymentTx?.amount ?? paymentTx?.total ?? 0)}
        subtotal={parseFloat(paymentTx?.subtotal ?? paymentTx?.amount ?? paymentTx?.total ?? 0)}
        discountAmount={parseFloat(paymentTx?.discount_amount ?? 0)}
        discountLabel={paymentTx?.discount_label ?? null}
        clientName={paymentTx?.user_name}
        clientEmail={paymentTx?.user_email}
        userId={paymentTx?.user_id}
        venueId={venueID}
        cartType={paymentTx?.cart_type ?? null}
        allCartTypes={paymentTx?._allCartTypes}
        token={token}
      />
    </div>
  );
}
